import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { 
  Calendar, 
  MapPin, 
  DollarSign, 
  Trophy, 
  Users, 
  Clock, 
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatDateOnly } from '../../utils/timezone';
import { formatDateOnlyDisplay } from '../../utils/businessTime';
import ImageLightbox from '../../components/shared/ImageLightbox';

const ParentTournamentDetail = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [existingRegistrations, setExistingRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  
  // Registration form state
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  // Stripe Checkout returns the parent here with `?payment=success` (or
  // `cancelled`). Surface a toast and strip the query so a refresh doesn't
  // fire it again. Same pattern used elsewhere in the app.
  useEffect(() => {
    const status = searchParams.get('payment');
    if (status === 'success') {
      toast.success('Payment successful — your registration is confirmed!');
      const next = new URLSearchParams(searchParams);
      ['payment', 'session_id', 'registration_id'].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled — you can try again anytime.');
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tournamentRes, playersRes, registrationsRes] = await Promise.all([
        api.get(`/tournaments/${tournamentId}`),
        api.get('/players'),
        api.get('/tournaments/my-registrations')
      ]);
      
      setTournament(tournamentRes.data);
      setPlayers(playersRes.data || []);
      
      // Filter registrations for this tournament
      const tournyRegs = (registrationsRes.data || []).filter(
        r => r.tournament_id === tournamentId
      );
      setExistingRegistrations(tournyRegs);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        toast.error('Tournament not found');
        navigate('/parent/tournaments');
      } else {
        toast.error('Failed to load tournament details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getEligiblePlayers = () => {
    if (!tournament || !players.length) return [];
    
    // Get IDs of already registered players
    const registeredPlayerIds = existingRegistrations.map(r => r.player_id);
    
    // Filter out already registered players
    return players.filter(p => !registeredPlayerIds.includes(p.id));
  };

  const getPlayerCategory = (player) => {
    if (!tournament || !player.date_of_birth) return null;

    // Pure YYYY-MM-DD math — never goes through browser-local Date so
    // the age never drifts by a day because of a UTC parse.
    const parts = (s) => String(s || '').split('-').map(Number);
    const [by, bm, bd] = parts(player.date_of_birth);
    const cutoffStr = tournament.age_cutoff_date || tournament.start_date;
    const [cy, cm, cd] = parts(cutoffStr);
    if (!by || !cy) return null;

    let age = cy - by;
    if (cm < bm || (cm === bm && cd < bd)) {
      age--;
    }
    
    // Determine category based on age
    for (const cat of tournament.age_categories || []) {
      const catAge = parseInt(cat.replace('u', ''));
      if (age < catAge) {
        return cat;
      }
    }
    return tournament.age_categories?.[tournament.age_categories.length - 1];
  };

  const handleRegister = async () => {
    if (!selectedPlayer) {
      toast.error('Please select a player');
      return;
    }
    if (!selectedCategory) {
      toast.error('Please select an age category');
      return;
    }
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    try {
      setRegistering(true);
      await api.post(`/tournaments/${tournamentId}/register`, {
        player_id: selectedPlayer,
        age_category: selectedCategory
      });
      
      toast.success('Registration submitted successfully!');
      setSelectedPlayer('');
      setSelectedCategory('');
      setAgreedToTerms(false);
      fetchData(); // Refresh to show new registration
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  const formatDateRange = (start, end) => {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${formatDateOnlyDisplay(start, options)} - ${formatDateOnlyDisplay(end, options)}`;
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-700',
    active: 'bg-green-100 text-green-700',
    future: 'bg-blue-100 text-blue-700',
    completed: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  if (loading) {
    return (
      <DashboardLayout title="Tournament Details">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!tournament) {
    return (
      <DashboardLayout title="Tournament Details">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Tournament not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/parent/tournaments')}>
            Back to Tournaments
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const eligiblePlayers = getEligiblePlayers();
  const canRegister = ['active', 'future'].includes(tournament.status) && eligiblePlayers.length > 0;

  return (
    <DashboardLayout title="Tournament Details">
      <div className="space-y-6" data-testid="tournament-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate('/parent/tournaments')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tournaments
        </Button>

        {/* Tournament Header */}
        <Card>
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Tournament Image */}
              {tournament.image_path ? (
                <div className="flex-shrink-0">
                  <ImageLightbox
                    src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`}
                    alt={tournament.name}
                    className="h-48 w-auto rounded-lg object-cover border shadow-sm"
                    testId="parent-tournament-image"
                  />
                </div>
              ) : (
                <div className="flex-shrink-0 h-48 w-48 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Trophy className="h-16 w-16 text-emerald-300" />
                </div>
              )}
              
              {/* Tournament Info */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-xl lg:text-2xl font-bold">{tournament.name}</h1>
                    <Badge className={statusColors[tournament.status]}>
                      {tournament.status}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline">
                      {tournament.location_type === 'out_of_state' ? 'Out of State' : 'In State'}
                    </Badge>
                    {tournament.location && (
                      <Badge variant="secondary" className="bg-slate-100">
                        <MapPin className="h-3 w-3 mr-1" />
                        {tournament.location}
                      </Badge>
                    )}
                    <Badge className={
                      tournament.tournament_style === 'competition' ? 'bg-purple-100 text-purple-700' :
                      tournament.tournament_style === 'recreational' ? 'bg-teal-100 text-teal-700' :
                      'bg-indigo-100 text-indigo-700'
                    }>
                      {tournament.tournament_style === 'competition' ? 'Competition' :
                       tournament.tournament_style === 'recreational' ? 'Recreational' : 'League'}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDateRange(tournament.start_date, tournament.end_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <DollarSign className="h-4 w-4" />
                    <span>{cs()}{tournament.initial_payment_amount} registration fee</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="h-4 w-4" />
                    <span>Registration closes: {formatDateOnly(tournament.registration_end_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="h-4 w-4" />
                    <span>Age Categories: {tournament.age_categories?.map(c => c.toUpperCase()).join(', ')}</span>
                  </div>
                </div>

                {tournament.details && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{tournament.details}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Existing Registrations */}
        {existingRegistrations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Your Registrations
              </CardTitle>
              <CardDescription>Players you've registered for this tournament</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {existingRegistrations.map((reg) => (
                  <div key={reg.id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{reg.player_name}</p>
                      <p className="text-sm text-slate-500">Category: {reg.age_category?.toUpperCase()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        reg.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {reg.payment_status}
                      </Badge>
                      <Badge variant="outline">{reg.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registration Form */}
        {canRegister ? (
          <Card>
            <CardHeader>
              <CardTitle>Register a Player</CardTitle>
              <CardDescription>Register your child for this tournament</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Player</Label>
                  <Select value={selectedPlayer} onValueChange={(val) => {
                    setSelectedPlayer(val);
                    // Auto-select category based on age
                    const player = players.find(p => p.id === val);
                    if (player) {
                      const suggestedCategory = getPlayerCategory(player);
                      if (suggestedCategory) setSelectedCategory(suggestedCategory);
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a player" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligiblePlayers.map((player) => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.first_name} {player.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Age Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {tournament.age_categories?.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-start space-x-2 pt-4">
                <Checkbox 
                  id="terms" 
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                />
                <label htmlFor="terms" className="text-sm text-slate-600 leading-tight">
                  I agree to the tournament terms and conditions. I understand the registration fee of {cs()}{tournament.initial_payment_amount} is required upon registration.
                </label>
              </div>

              <Button 
                className="w-full md:w-auto"
                onClick={handleRegister}
                disabled={registering || !selectedPlayer || !selectedCategory || !agreedToTerms}
              >
                {registering ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Trophy className="h-4 w-4 mr-2" />
                    Register Player - {cs()}{tournament.initial_payment_amount}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : !['active', 'future'].includes(tournament.status) ? (
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Registration is not currently open for this tournament.</p>
            </CardContent>
          </Card>
        ) : eligiblePlayers.length === 0 && existingRegistrations.length > 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <p className="text-slate-500">All your players are registered for this tournament!</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No eligible players found. Please add players to your family first.</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/parent/my-family')}>
                Manage My Family
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParentTournamentDetail;
